import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Check, Loader2 } from "lucide-react";
import { MODE_LABEL, modeColor } from "@/lib/gamemode";

const MODES = ["main", "ironman", "hardcore", "ultimate", "group", "hardcore_group"];

export function GamemodeDropdown({ value, onChange, changing = false }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const place = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 208) });
  };

  useLayoutEffect(() => { if (open) place(); }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (triggerRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    const onScroll = () => place();
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  const pick = (gm) => {
    setOpen(false);
    if (gm !== value) onChange(gm);
  };

  return (
    <div className="relative inline-block" data-testid="profile-gamemode">
      <button
        type="button"
        ref={triggerRef}
        data-testid="gamemode-trigger"
        onClick={() => setOpen((o) => !o)}
        disabled={changing}
        title="Change account type (e.g. after an HCIM death)"
        className="group inline-flex items-center gap-2 osrs-panel-plain px-3 py-1 border-2 border-black shadow-[inset_0_1px_0_rgba(255,232,170,0.18)] hover:brightness-110 disabled:opacity-70 transition-[filter]"
      >
        <span className="w-2.5 h-2.5 rounded-full border border-black/60 shrink-0" style={{ backgroundColor: modeColor(value) }} />
        <span className="osrs-num osrs-shadow text-lg leading-none" style={{ color: modeColor(value) }}>
          {MODE_LABEL[value]} <span className="text-[#b9a06a] text-sm">account</span>
        </span>
        {changing
          ? <Loader2 size={15} className="text-[#E6A519] animate-spin" />
          : <ChevronDown size={15} className={`text-[#E6A519] transition-transform ${open ? "rotate-180" : ""}`} />}
      </button>

      {open && pos && createPortal(
        <div
          ref={menuRef}
          data-testid="gamemode-menu"
          className="fixed z-[100] osrs-panel p-1 shadow-[3px_3px_0_rgba(0,0,0,0.6)]"
          style={{ top: pos.top, left: pos.left, width: pos.width }}
        >
          {MODES.map((gm) => {
            const active = gm === value;
            return (
              <button
                key={gm}
                type="button"
                data-testid={`gamemode-option-${gm}`}
                onClick={() => pick(gm)}
                className={`w-full flex items-center gap-2 px-2.5 py-2 text-left border border-transparent transition-colors ${active ? "bg-[#3a2d18] border-black" : "hover:bg-[#2b2114]"}`}
              >
                <span className="w-2.5 h-2.5 rounded-full border border-black/60 shrink-0" style={{ backgroundColor: modeColor(gm) }} />
                <span className="osrs-num osrs-shadow text-sm flex-1 leading-none" style={{ color: modeColor(gm) }}>
                  {MODE_LABEL[gm]}
                </span>
                {active && <Check size={14} className="text-[#7CFC7C]" />}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}

export default GamemodeDropdown;
